import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation, languages } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clover, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    currency: 'EUR',
    language: 'es'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const t = useTranslation(formData.language);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const response = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        currency: formData.currency,
        language: formData.language
      });
      // New flow: backend returns pending_verification, redirect to verify page
      if (response?.data?.status === 'pending_verification' || response?.status === 'pending_verification') {
        toast.success('¡Registro completado! Revisa tu email para el código de verificación.');
        navigate('/verify-email', { state: { email: formData.email } });
      } else {
        // Fallback for unexpected responses
        toast.success(t('registerSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const currencies = [
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'DOP', symbol: 'RD$', name: 'Peso Dominicano' },
    { code: 'COP', symbol: '$', name: 'Peso Colombiano' },
    { code: 'MXN', symbol: '$', name: 'Peso Mexicano' },
    { code: 'ARS', symbol: '$', name: 'Peso Argentino' },
    { code: 'CAD', symbol: 'C$', name: 'Dólar Canadiense' },
    { code: 'BRL', symbol: 'R$', name: 'Real Brasileño' },
    { code: 'CLP', symbol: '$', name: 'Peso Chileno' },
    { code: 'PEN', symbol: 'S/', name: 'Sol Peruano' },
    { code: 'UYU', symbol: '$U', name: 'Peso Uruguayo' },
    { code: 'JPY', symbol: '¥', name: 'Yen Japonés' },
    { code: 'CNY', symbol: '¥', name: 'Yuan Chino' },
    { code: 'INR', symbol: '₹', name: 'Rupia India' },
    { code: 'AUD', symbol: 'A$', name: 'Dólar Australiano' },
    { code: 'SEK', symbol: 'kr', name: 'Corona Sueca' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 xl:px-24 bg-white py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-emerald-900 rounded-sm flex items-center justify-center">
              <Clover className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display font-bold text-xl tracking-tight text-slate-900">
              Trebol Finance by Leapifyes
            </span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight mb-2">
              {t('register')}
            </h1>
            <p className="text-slate-500 text-sm">
              Comienza tu control financiero total
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('name')}
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Tu nombre"
                className="border-0 border-b border-slate-200 rounded-none px-0 py-3 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="nombre@ejemplo.com"
                className="border-0 border-b border-slate-200 rounded-none px-0 py-3 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  {t('password')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="••••••••"
                    className="border-0 border-b border-slate-200 rounded-none px-0 py-3 pr-10 text-slate-900 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300"
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  {t('confirmPassword')}
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="••••••••"
                  className="border-0 border-b border-slate-200 rounded-none px-0 py-3 text-slate-900 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  {t('currency')}
                </Label>
                <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)}>
                  <SelectTrigger className="border-0 border-b border-slate-200 rounded-none px-0 py-3 text-slate-900 focus:ring-0" data-testid="register-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  {t('language')}
                </Label>
                <Select value={formData.language} onValueChange={(v) => handleChange('language', v)}>
                  <SelectTrigger className="border-0 border-b border-slate-200 rounded-none px-0 py-3 text-slate-900 focus:ring-0" data-testid="register-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(l => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-sm uppercase tracking-widest text-xs font-bold py-6 transition-colors mt-6"
              data-testid="register-submit-btn"
            >
              {loading ? t('loading') : t('register')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          {/* Login link */}
          <p className="mt-6 text-center text-sm text-slate-500">
            {t('hasAccount')}{' '}
            <Link
              to="/login"
              className="text-emerald-900 font-semibold hover:underline"
              data-testid="login-link"
            >
              {t('login')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/80" />
        <div className="absolute inset-0 flex flex-col justify-center items-center p-12 text-center">
          <Clover className="w-16 h-16 text-white/80 mb-6" strokeWidth={1} />
          <h2 className="text-white text-2xl font-display font-bold mb-4">
            Control Total de tu Dinero
          </h2>
          <ul className="text-white/70 text-sm space-y-2 text-left">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              Registro hasta el último céntimo
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              Categorización inteligente
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              Análisis y tendencias
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              Asistente IA financiero
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Register;
